import { useId } from 'react';
import { useTranslation } from 'react-i18next';
import { Link, useParams } from 'react-router';

import { HelpButton } from '../components/HelpButton.js';
import { NotFound } from '../components/Layouts.js';
import {
  PaybackChart,
  useFormat,
  hi,
  lo,
  noSpeedGain,
  useModernization,
  type ModernPackage,
  type Modernization,
} from '../components/modernization.js';
import { LinkButton, LoadError, Loading, TableBox } from '../components/ui.js';
import { errorMessage } from '../errors.js';

/** Who can do a task: the owner with a plugin, a developer, the host, or we do it ourselves. */
const WHO: Record<string, 'you' | 'developer' | 'host' | 'us' | 'designer'> = {
  images: 'you',
  lazy_loading: 'you',
  unused_code: 'developer',
  cache_compression: 'host',
  verify: 'us',
  cms_update: 'developer',
  php_upgrade: 'host',
  security_headers: 'developer',
  accessibility: 'developer',
  backup_test: 'developer',
  local_business: 'you',
  titles_descriptions: 'you',
  open_graph: 'developer',
  relaunch_design: 'designer',
  relaunch_build: 'developer',
  content_migration: 'developer',
  redirects_seo: 'developer',
  relaunch_testing: 'developer',
};

/**
 * One modernization package (T-39, site-modernization.html, package): now, meaning, steps and
 * gain; before → after; hours by job; the payback; what we need and the way on.
 */
export function ModernizationPackage() {
  const { siteId = '', packageKey = '' } = useParams();
  const { t } = useTranslation();
  const f = useFormat();
  const query = useModernization(siteId);

  if (query.isPending) return <Loading />;
  if (query.isError)
    return (
      <LoadError message={errorMessage(query.error, t)} onRetry={() => void query.refetch()} />
    );
  const m = query.data;
  const p = m.packages.find((x) => x.key === packageKey);
  if (!p) return <NotFound />;

  return (
    <>
      <div className="area-hd">
        <Link className="crumb" to={`/sites/${siteId}/modernization`}>
          <span aria-hidden="true">← </span>
          {t('modern.back')}
        </Link>
        <h1>{t(`modern.packages.${p.key}.title`)}</h1>
        <div className="sub">
          {p.recommended ? (
            <span className="badge acc nodot">{t('modern.recommended')}</span>
          ) : null}
          <span>
            {t('modern.pkgSub', { hours: f.hours(p.hours.ai), cost: f.euroRange(p.cost.ai) })}
            {p.paybackMonths
              ? ` · ${t('modern.pkgPayback', { value: f.months(p.paybackMonths) })}`
              : null}
          </span>
        </div>
      </div>

      <div className="four">
        <div>
          <b>{t('area.now')}</b>
          {p.findings.length > 0 ? (
            <ul>
              {p.findings.map((x) => (
                <li key={x.ruleCode}>{x.title}</li>
              ))}
            </ul>
          ) : (
            <p>{t(`modern.packages.${p.key}.now`)}</p>
          )}
        </div>
        <div>
          <b>{t('area.means')}</b>
          <p>{t(`modern.packages.${p.key}.means`)}</p>
        </div>
        <div>
          <b>{t('area.todo')}</b>
          <ol>
            {p.tasks.map((task) => (
              <li key={task.key}>{t(`modern.tasks.${task.key}`)}</li>
            ))}
          </ol>
        </div>
        <div>
          <b>{t('area.gain')}</b>
          <p>{t(`modern.packages.${p.key}.gain`)}</p>
        </div>
      </div>

      <Effect p={p} />
      <Hours p={p} rate={m.figures.hourlyRate} />
      <Payback p={p} figures={m.figures} lcpMs={m.facts.lcpMs} />

      <Needs siteId={siteId} p={p} />
    </>
  );
}

/** Measured now and our estimate afterwards – only what the code computes (audit §2). */
function Effect({ p }: { p: ModernPackage }) {
  const { t } = useTranslation();
  const f = useFormat();
  const id = useId();
  const e = p.effect;
  const rows: [string, string][] = [];
  if (e.lcpMs)
    rows.push([t('modern.facts.lcp'), `${f.seconds(lo(e.lcpMs))} → ≈ ${f.seconds(hi(e.lcpMs))}`]);
  if (e.pageBytes)
    rows.push([
      t('modern.facts.weight'),
      `${f.megabytes(lo(e.pageBytes))} → ≈ ${f.megabytes(hi(e.pageBytes))}`,
    ]);
  if (e.score) rows.push([t('modern.facts.score'), `${lo(e.score)} → ≈ ${hi(e.score)} / 100`]);
  if (rows.length === 0) return null;
  return (
    <section className="card" aria-labelledby={id}>
      <h2 id={id}>
        {t('modern.effectTitle')} <small>{t('modern.effectHint')}</small>
      </h2>
      <dl className="kv">
        {rows.map(([label, value]) => (
          <div key={label}>
            <dt>{label}</dt>
            <dd className="mono">{value}</dd>
          </div>
        ))}
      </dl>
    </section>
  );
}

/** "Stunden nach Arbeiten": each job, who can do it, hours with AI and classic. */
function Hours({ p, rate }: { p: ModernPackage; rate: number }) {
  const { t } = useTranslation();
  const f = useFormat();
  const id = useId();
  return (
    <section className="card" aria-labelledby={id}>
      <h2 id={id}>
        {t('modern.hoursTitle')} <small>{t('modern.perHour', { value: f.euro(rate) })}</small>
      </h2>
      <TableBox label={t('modern.hoursTitle')}>
        <table className="hours">
          <thead>
            <tr>
              <th scope="col">{t('modern.table.task')}</th>
              <th scope="col">{t('modern.table.who')}</th>
              <th scope="col" className="num">
                {t('modern.table.ai')}
              </th>
              <th scope="col" className="num">
                {t('modern.table.classic')}
              </th>
            </tr>
          </thead>
          <tbody>
            {p.tasks.map((task) => (
              <tr key={task.key}>
                <td>{t(`modern.tasks.${task.key}`)}</td>
                <td>{t(`modern.who.${WHO[task.key] ?? 'developer'}`)}</td>
                <td className="num">{t('modern.hours', { value: f.num(task.aiHours) })}</td>
                <td className="num">{t('modern.hours', { value: f.num(task.classicHours) })}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </TableBox>
      <p className="spec">
        <b>{t('modern.aiHelpTitle')}:</b> {t(`modern.packages.${p.key}.aiHelp`)}
      </p>
    </section>
  );
}

/** "Amortisation" (.roi): the chart with the customer's figures, or why there is none. */
function Payback({
  p,
  figures,
  lcpMs,
}: {
  p: ModernPackage;
  figures: Modernization['figures'];
  lcpMs: number | null;
}) {
  const { t } = useTranslation();
  const f = useFormat();
  const id = useId();
  const chart =
    p.monthlyGain && p.paybackMonths ? { gain: p.monthlyGain, payback: p.paybackMonths } : null;
  const still = noSpeedGain(p, lcpMs);
  return (
    <section className="card roi" aria-labelledby={id}>
      <h2 id={id}>
        {t('modern.payback.title')}
        {chart ? (
          <>
            {' '}
            <small>
              {t('modern.payback.figures', {
                visitors: f.num(figures.monthlyVisitors ?? 0, 0),
                conversion: f.num(figures.conversionPct ?? 0, 2),
                order: f.euro(figures.avgOrderEur ?? 0),
              })}
            </small>
          </>
        ) : null}
      </h2>
      {chart ? (
        <>
          <PaybackChart cost={hi(p.cost.ai)} gain={chart.gain} payback={chart.payback} />
          <p className="spec">{t('modern.payback.explain')}</p>
        </>
      ) : still ? (
        // No time to win on the home page: no figures make it money (SP-A2.71).
        <p className="bodyM">
          {t(still === 'fast' ? 'modern.payback.fastLong' : 'modern.payback.notMeasuredLong')}
        </p>
      ) : p.uplift && !p.monthlyGain ? (
        <p className="bodyM">{t('modern.payback.needFiguresLong')}</p>
      ) : p.monthlyGain ? (
        // Figures entered, but the cautious gain rounds to nothing: it does not pay back.
        <p className="bodyM">{t('modern.payback.tooSmall')}</p>
      ) : (
        <p className="bodyM">{t(`modern.packages.${p.key}.paybackLong`)}</p>
      )}
      <p className="spec">
        {t('modern.sources.title')}: {t('modern.sources.deloitte')} {t('modern.sources.google')}{' '}
        {t('modern.sources.hours')}
      </p>
    </section>
  );
}

/** "Was wir von Ihnen brauchen": access, consent, and help or doing it oneself. */
function Needs({ siteId, p }: { siteId: string; p: ModernPackage }) {
  const { t } = useTranslation();
  const id = useId();
  return (
    <section className="card" aria-labelledby={id}>
      <h2 id={id}>{t('modern.accessTitle')}</h2>
      <ul className="steps-list">
        {t(`modern.packages.${p.key}.access`)
          .split('\n')
          .map((line) => (
            <li key={line}>{line}</li>
          ))}
      </ul>
      <p className="spec">{t('modern.consent')}</p>
      <div className="acts">
        {p.service ? <HelpButton siteId={siteId} service={p.service} /> : null}
        <LinkButton to={`/sites/${siteId}/findings`} variant="secondary">
          {t('modern.diy')}
        </LinkButton>
      </div>
      <p className="spec">{t('modern.disclaimer')}</p>
    </section>
  );
}
